import React, { useCallback, useMemo } from "react"
import PropTypes from "prop-types"
import { useNavigate } from "react-router-dom"
import DataTable from "ui/DataTable"
import { useSelector } from "react-redux"
import { routes } from "router/routes"
import { useTranslation } from "react-i18next"
import { createEquipmentColumns } from "./columns"
import WithEmptyStateWrapper from "ui/WithEmptyStateWrapper"
import { DSIcon } from "ui/Icon/DSIcon"
import { usePermissions } from "hooks/usePermissions"
import { MODULES, ACTIONS } from "utils/permissions"
import styles from "./index.module.css"

// Example data, replace with your real data source
export const dataSource = []

const EquipmentTable = ({ data = dataSource, onChange, onDeactivate, onDelete, onActivate }) => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { hasPermission } = usePermissions()
  const userRole = useSelector((state) => state.user.role)

  const handleEdit = useCallback((record) => {
    navigate(routes.guideEquipmentEditPath({ id: record.key }))
  }, [navigate])

  const handleDuplicate = useCallback((record) => {
    navigate(routes.guideEquipmentDuplicatePath({ id: record.key }))
  }, [navigate])

  const handleTitleClick = useCallback(
    (record) => {
      navigate(routes.guideEquipmentDetailsPath({ id: record.key }))
    },
    [navigate]
  )

  const columns = useMemo(
    () =>
      createEquipmentColumns(
        { onEdit: handleEdit, onDeactivate, onDelete, onActivate, onDuplicate: handleDuplicate, onTitleClick: handleTitleClick },
        t,
        { hasPermission, MODULES, ACTIONS, userRole }
      ),
    [handleEdit, onDeactivate, onDelete, onActivate, handleDuplicate, handleTitleClick, t, hasPermission, userRole]
  )

  return (
    <section className={styles.equipmentTable}>
      <WithEmptyStateWrapper
        name="equipment"
        hasItems={data?.length > 0}
        fallbackTitle={t("equipment.empty_state.title", { defaultValue: "No equipment added yet" })}
        fallbackDescription={t("equipment.empty_state.description", { defaultValue: "Add your first equipment to get started" })}
        fallbackImageSrc={<DSIcon name="boat" width={80} height={80} />}
      >
        <DataTable columns={columns} dataSource={data} pagination={false} onChange={onChange} />
      </WithEmptyStateWrapper>
    </section>
  )
}

EquipmentTable.propTypes = {
  data: PropTypes.array.isRequired,
  onChange: PropTypes.func,
  onDeactivate: PropTypes.func,
  onDelete: PropTypes.func,
  onActivate: PropTypes.func
}

export default React.memo(EquipmentTable)
